import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getToken, setToken, clearToken } from "../lib/api";

const AuthContext = createContext(null);

// NOTE: permissions are now sourced from the backend (/auth/me returns
// `permissions: []`). No parallel map on the FE — single source of truth is
// `auth.py:ROLE_PERMS`. This eliminates the drift problem that caused
// admin/sales action buttons to silently disappear after Iter 18.

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [permissions, setPermissions] = useState(new Set());
    const [featureFlags, setFeatureFlags] = useState({});
    const [loading, setLoading] = useState(true);

    const setAuthedUser = useCallback((u, perms, flags) => {
        setUser(u);
        setPermissions(new Set(perms || []));
        setFeatureFlags(flags || {});
    }, []);

    const clearAuthedUser = useCallback(() => {
        setUser(null);
        setPermissions(new Set());
        setFeatureFlags({});
    }, []);

    const refresh = useCallback(async () => {
        if (!getToken()) {
            clearAuthedUser();
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setAuthedUser(data, data.permissions, data.feature_flags);
        } catch {
            clearAuthedUser();
            clearToken();
        } finally {
            setLoading(false);
        }
    }, [setAuthedUser, clearAuthedUser]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = useCallback(
        async (email, password) => {
            const { data } = await api.post("/auth/login", { email, password });
            setToken(data.token);
            // Login response does not carry feature_flags (kept off the
            // wire for W1-D minimal-surface principle). AuthContext
            // hydrates them via the /me follow-up call below.
            setAuthedUser(data.user, data.permissions, {});
            // Iter 99 fix — surface the permissions array to the caller
            // (Login.jsx) so the landing-route decision can read it
            // synchronously, before /auth/me hydrates the in-state Set.
            return { user: data.user, permissions: data.permissions || [] };
        },
        [setAuthedUser]
    );

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch (_e) {
            // ignore — stateless JWT
        }
        clearToken();
        clearAuthedUser();
    }, [clearAuthedUser]);

    const can = useCallback((perm) => permissions.has(perm), [permissions]);
    // UAT-02 (2026-07-14) — OR-gate helper mirroring the backend
    // `require_perms_any`. Manager surfaces (e.g. Regularization
    // Requests decide-actions column) use this so a pure Manager
    // persona holding only `manager:read` sees the same UI affordances
    // as an HR user holding `employee:read`. No new permission code
    // introduced — Blueprint v2.4 unchanged.
    const canAny = useCallback(
        (perms) => (perms || []).some((p) => permissions.has(p)),
        [permissions],
    );

    // RPDM W1-D — feature-flag reader. Returns false for unknown flag
    // names so a caller can safely gate a UI element on a flag the
    // backend has not yet published.
    //
    // RPDM W2-UI · Phase 1a JSDoc addendum (2026-07-31 · § 11.0-A).
    // The following JSDoc block is documentation-only and does not
    // change runtime behaviour. Its purpose is to enumerate the eight
    // Wave 2 client feature flag names that `useRpdmWave2Gates`
    // (src/hooks/useRpdmWave2Gates.js) composes off of `isFlagEnabled`.
    // Traceability anchor for FC-6 (server-authoritative gating).
    //
    // Wave 2 client feature flags (composed by `useRpdmWave2Gates`):
    //   - rpdm_workspace_enabled          (W1 master · shared)
    //   - rpdm_w2_master_enabled          (W2 master kill-switch)
    //   - rpdm_w2_hierarchy_enabled       (W2-A / W2-B surfaces)
    //   - rpdm_w2_reviewer_enabled        (W2-C reviewer tab)
    //   - rpdm_w2_evaluator_authoritative (W2-D lifecycle authority)
    //   - rpdm_w2_dependency_enabled      (W2-E deps list + graph)
    //   - rpdm_w2_progress_enabled        (W2-F progress surfaces)
    //   - rpdm_w2_effort_enabled          (W2-G effort ledger)
    //   - rpdm_w2_effort_analytics_enabled (W2-G analytics · child of effort)
    //
    // Every flag remains server-authoritative. This client cache is a
    // UX convenience only; a server 404 with code=feature_not_enabled
    // still fails the associated surface closed (Rev.2 § 13.3).
    const isFlagEnabled = useCallback(
        (name) => featureFlags[name] === true,
        [featureFlags],
    );

    return (
        <AuthContext.Provider value={{
            user, permissions, featureFlags, loading,
            login, logout, can, canAny, isFlagEnabled, refresh,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
};

export const ROLE_LABELS = {
    super_admin: "Super Admin",
    accounts: "Accounts",
    sales: "Sales",
    viewer: "Viewer",
    data_user: "Data User",
    // Iter 70 — Repository operator role (subset of data_user).
    // Can maintain companies/logos, run dry-run enrichment, and verify
    // records — but cannot import, export, or launch live enrichment.
    logo_user: "Logo User",
};
